/**
 * CalendarPage — The Planning Room.
 *
 * Residence Journal · a quiet family diary of the week ahead.
 * The first thing shown is *This Week* — days as a list, not a grid.
 * Below that, longer-range planning.
 *
 * Presentation only. All handlers and state hooks preserved.
 */
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import BetaFeedbackButton from "../../components/BetaFeedbackButton";
import { useHomeOSState } from "./state/HomeOSStateProvider";
import { TaskEditModal } from "./components/TaskEditModal";
import {
  ResidenceLayout, PageHeading, EditorialSection,
  TextLink, PillButton, Colophon, useLightPhase,
} from "./components/os/editorial";
import { ResidenceRail } from "./components/os/ResidenceRail";
import { getUpcomingFamilyBirthdays, birthdayRelative, birthdayFocalSentence } from "./utils/birthdayIntelligence";

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function toISO(d) { return d.toISOString().slice(0, 10); }
function isoToDate(iso) { return new Date(iso + "T00:00:00"); }
function formatShort(d) {
  return d.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' });
}
function daysUntil(iso) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  const d = isoToDate(iso); d.setHours(0, 0, 0, 0);
  return Math.round((d - now) / 86400000);
}

/* A derived birthday in Coming Up — read straight from the Family Profile.
   Not a task: no edit, no dismiss. Gift ideas expand read-only. */
function BirthdayComingUpRow({ b, className }) {
  const [showIdeas, setShowIdeas] = useState(false);
  const ideas = [...b.giftIdeas, ...b.wants];
  const showGiftContext = b.daysUntil <= 42 && ideas.length > 0;
  return (
    <li
      className={className}
      style={{ borderColor: 'var(--ara-line-soft)' }}
      data-testid={`calendar-later-birthday-${b.memberId}`}
    >
      <div>
        <p
          className="text-[13.5px] leading-tight"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
        >
          {b.date.toLocaleDateString('en-NZ', { day: 'numeric', month: 'short' })}
        </p>
        <p className="caption-ara mt-0.5">{birthdayRelative(b.daysUntil)}</p>
      </div>
      <div className="min-w-0">
        <p
          className="text-[14.5px] leading-tight"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
        >
          {b.memberName}'s birthday
        </p>
        <p
          className="mt-0.5 text-[12.5px]"
          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
          data-testid={`calendar-birthday-gift-count-${b.memberId}`}
        >
          Birthday
          {showGiftContext && ` · ${ideas.length} gift idea${ideas.length === 1 ? '' : 's'} saved`}
        </p>
        {showIdeas && (
          <ul className="mt-2 space-y-1" data-testid={`calendar-birthday-ideas-${b.memberId}`}>
            {ideas.map((idea, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-[13px] leading-[1.5]"
                style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)' }}
              >
                <span className="w-1 h-1 rounded-full mt-2 flex-shrink-0" style={{ background: 'var(--ara-ink-soft)' }} />
                {idea}
              </li>
            ))}
          </ul>
        )}
      </div>
      {showGiftContext ? (
        <TextLink
          onClick={() => setShowIdeas(s => !s)}
          testid={`calendar-birthday-view-ideas-${b.memberId}`}
        >
          {showIdeas ? 'Hide ideas' : 'View ideas →'}
        </TextLink>
      ) : (
        <Link to="/home-os/family" className="link-ara text-[13px]" data-testid={`calendar-birthday-open-profile-${b.memberId}`}>
          Open profile →
        </Link>
      )}
    </li>
  );
}

export default function CalendarPage() {
  const { phase } = useLightPhase();
  const { tasks, members } = useHomeOSState();
  const [editingTask, setEditingTask] = useState(null);

  const todayDate = new Date();
  const todayIso = toISO(todayDate);

  const memberById = useMemo(() => {
    const m = {}; (members || []).forEach(x => { m[x.id] = x; }); return m;
  }, [members]);

  /* Birthdays — derived from Family Profiles, never duplicated. */
  const upcomingBirthdays = useMemo(
    () => getUpcomingFamilyBirthdays(members, todayDate),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [members, todayIso]
  );
  const birthdaysByIso = useMemo(() => {
    const map = {};
    upcomingBirthdays.forEach(b => { (map[b.iso] = map[b.iso] || []).push(b); });
    return map;
  }, [upcomingBirthdays]);
  /* Coming Up window: beyond this week, within 8 weeks — quiet before that. */
  const comingUpBirthdays = useMemo(
    () => upcomingBirthdays.filter(b => b.daysUntil >= 7 && b.daysUntil <= 56),
    [upcomingBirthdays]
  );

  /* Build a list of the next 7 days starting today */
  const week = useMemo(() => {
    const days = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(todayDate); d.setDate(d.getDate() + i);
      const iso = toISO(d);
      days.push({
        iso,
        date: d,
        dayName: DAY_NAMES[d.getDay()],
        events: (tasks || []).filter(t => t.dueDate === iso).sort((a, b) => (a.dueTime || '').localeCompare(b.dueTime || '')),
      });
    }
    return days;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, todayIso]);

  /* Later — anything scheduled beyond the 7-day window */
  const later = useMemo(() => {
    const cutoff = new Date(todayDate); cutoff.setDate(cutoff.getDate() + 7);
    const cutoffIso = toISO(cutoff);
    return (tasks || [])
      .filter(t => t.dueDate && t.dueDate >= cutoffIso)
      .sort((a, b) => (a.dueDate || '').localeCompare(b.dueDate || ''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks, todayIso]);

  const totalWeekEvents = week.reduce((sum, d) => sum + d.events.length, 0);
  const weekBirthdayCount = week.reduce((sum, d) => sum + (birthdaysByIso[d.iso]?.length || 0), 0);
  const weekHasContent = totalWeekEvents > 0 || weekBirthdayCount > 0;

  /* ARA thinks before it speaks — birthdays compete with the week's load. */
  const birthdayLine = birthdayFocalSentence(upcomingBirthdays);
  const nearestBirthday = upcomingBirthdays[0] || null;
  let focal;
  if (birthdayLine && nearestBirthday && nearestBirthday.daysUntil < 7) {
    focal = birthdayLine;
  } else if (totalWeekEvents === 0) {
    focal = birthdayLine || "The week ahead is open. Add the first plan when you're ready.";
  } else if (birthdayLine && nearestBirthday && nearestBirthday.daysUntil <= 21) {
    focal = `${totalWeekEvents} thing${totalWeekEvents === 1 ? "" : "s"} this week — and ${birthdayLine.charAt(0).toLowerCase()}${birthdayLine.slice(1)}`;
  } else {
    focal = totalWeekEvents === 1
      ? "One thing on the week ahead."
      : totalWeekEvents <= 4
        ? `${totalWeekEvents} things on the week ahead — a gentle rhythm.`
        : `${totalWeekEvents} things on the week ahead — worth a quick scan.`;
  }

  return (
    <ResidenceLayout rail={<ResidenceRail currentKey="today" />} phase={phase} testid="module-page-calendar">
      <PageHeading
        testid="calendar-hero"
        module="today"
        room="Planning"
        title="The Planning Room"
        focal={focal}
        actions={
          <>
            <PillButton
              variant="solid"
              onClick={() => setEditingTask({ taskId: null, mode: "add", presetDate: todayIso })}
              testid="calendar-add-event-btn"
            >
              Add plan
            </PillButton>
            <TextLink onClick={() => window.location.href = "/home-os/chores"} testid="calendar-view-chores">
              Household tasks
            </TextLink>
          </>
        }
      />

      {/* Preserved testids */}
      <div className="hidden" data-testid="module-title" aria-hidden>Planning</div>
      <div className="hidden" data-testid="module-subtitle" aria-hidden>The family diary of the week ahead.</div>

      {/* ── This Week — days as a diary list ───────────────────────── */}
      <EditorialSection
        testid="calendar-this-week"
        moduleMark={{ module: 'today', label: 'This week' }}
        title="This Week"
        aside={
          totalWeekEvents > 0
            ? <p className="caption-ara" data-testid="calendar-week-count">{totalWeekEvents} planned</p>
            : null
        }
      >
        {!weekHasContent ? (
          <div>
            <p
              className="italic text-[17px] sm:text-[19px] leading-[1.4]"
              style={{
                fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
                color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '36ch',
              }}
              data-testid="calendar-empty-sentence"
            >
              A family diary begins with a single plan. Add the first.
            </p>
            <div className="mt-6">
              <PillButton
                variant="solid"
                onClick={() => setEditingTask({ taskId: null, mode: "add", presetDate: todayIso })}
                testid="calendar-add-first"
              >
                Add the first plan
              </PillButton>
            </div>
          </div>
        ) : (
          <div>
            {week.map((day, i) => {
              const isToday = day.iso === todayIso;
              const isLast = i === week.length - 1;
              const dayBirthdays = birthdaysByIso[day.iso] || [];
              return (
                <div
                  key={day.iso}
                  className={`py-4 ${i === 0 ? 'border-t' : ''} ${isLast ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                  data-testid={`calendar-day-${day.iso}`}
                >
                  <div className="grid grid-cols-[120px_1fr] gap-6 items-baseline">
                    <div className="min-w-0">
                      <p
                        className="text-[14px] leading-tight"
                        style={{
                          fontFamily: 'Inter, system-ui, sans-serif',
                          color: isToday ? 'var(--ara-ink)' : 'var(--ara-ink-muted)',
                          fontWeight: isToday ? 500 : 400,
                        }}
                      >
                        {day.dayName}
                      </p>
                      <p className="caption-ara mt-0.5">
                        {formatShort(day.date)}{isToday ? ' · today' : ''}
                      </p>
                    </div>
                    <div className="min-w-0">
                      {dayBirthdays.length > 0 && (
                        <ul className={`space-y-2 ${day.events.length > 0 ? 'mb-2' : ''}`}>
                          {dayBirthdays.map(b => (
                            <li key={`bday-${b.memberId}`} data-testid={`calendar-birthday-${b.memberId}`}>
                              <p
                                className="text-[14.5px] leading-tight"
                                style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                              >
                                {b.daysUntil === 0 ? `Happy birthday, ${b.memberName}.` : `${b.memberName}'s birthday`}
                              </p>
                              <p
                                className="mt-0.5 text-[12.5px]"
                                style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                              >
                                Birthday · Family
                                {b.giftIdeas.length > 0 && ` · ${b.giftIdeas.length} gift idea${b.giftIdeas.length === 1 ? '' : 's'} saved`}
                              </p>
                            </li>
                          ))}
                        </ul>
                      )}
                      {day.events.length === 0 && dayBirthdays.length === 0 ? (
                        <button
                          type="button"
                          onClick={() => setEditingTask({ taskId: null, mode: "add", presetDate: day.iso })}
                          className="text-[13px] transition-ara hover:opacity-70"
                          style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-soft)', fontWeight: 400 }}
                          data-testid={`calendar-add-day-${day.iso}`}
                        >
                          + Add
                        </button>
                      ) : (
                        <ul className="space-y-2">
                          {day.events.map(ev => {
                            const owner = ev.assignedTo ? memberById[ev.assignedTo] : null;
                            return (
                              <li key={ev.id} data-testid={`calendar-event-${ev.id}`}>
                                <button
                                  type="button"
                                  onClick={() => setEditingTask({ taskId: ev.id, mode: "edit" })}
                                  className="w-full text-left transition-ara hover:opacity-70"
                                >
                                  <p
                                    className="text-[14.5px] leading-tight"
                                    style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                                  >
                                    {ev.title}
                                  </p>
                                  <p
                                    className="mt-0.5 text-[12.5px]"
                                    style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                                  >
                                    {ev.dueTime ? `${ev.dueTime} · ` : ''}{owner ? owner.name.split(' ')[0] : (ev.category || 'Family')}
                                  </p>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </EditorialSection>

      {/* ── Coming up (beyond this week) — one household timeline ──── */}
      <EditorialSection
        testid="calendar-later"
        moduleMark={{ module: 'today', label: 'Later' }}
        title="Coming up"
      >
        {later.length === 0 && comingUpBirthdays.length === 0 ? (
          <p
            className="italic text-[16px] leading-[1.5]"
            style={{
              fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
              color: 'var(--ara-ink-muted)', fontWeight: 400, maxWidth: '44ch',
            }}
          >
            Nothing scheduled beyond this week. When something's on the horizon, ARA will surface it here.
          </p>
        ) : (
          <ul>
            {[
              ...later.slice(0, 12).map(ev => ({ kind: 'event', iso: ev.dueDate, ev })),
              ...comingUpBirthdays.map(b => ({ kind: 'birthday', iso: b.iso, b })),
            ]
              .sort((a, z) => (a.iso || '').localeCompare(z.iso || ''))
              .map((item, i, arr) => {
                const rowCls = `grid grid-cols-[120px_1fr_auto] items-baseline gap-4 py-3 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`;
                if (item.kind === 'birthday') {
                  return (
                    <BirthdayComingUpRow key={`bday-${item.b.memberId}`} b={item.b} className={rowCls} />
                  );
                }
                const ev = item.ev;
                const d = ev.dueDate ? isoToDate(ev.dueDate) : null;
                const owner = ev.assignedTo ? memberById[ev.assignedTo] : null;
                return (
                <li
                  key={ev.id}
                  className={`grid grid-cols-[120px_1fr_auto] items-baseline gap-4 py-3 ${i === 0 ? 'border-t' : ''} ${i === arr.length - 1 ? '' : 'border-b'}`}
                  style={{ borderColor: 'var(--ara-line-soft)' }}
                  data-testid={`calendar-later-${ev.id}`}
                >
                  <div>
                    <p
                      className="text-[13.5px] leading-tight"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                    >
                      {d ? d.toLocaleDateString('en-NZ', { weekday: 'short', day: 'numeric', month: 'short' }) : '—'}
                    </p>
                    <p className="caption-ara mt-0.5">in {daysUntil(ev.dueDate)}d</p>
                  </div>
                  <div className="min-w-0">
                    <p
                      className="text-[14.5px] leading-tight"
                      style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink)', fontWeight: 500 }}
                    >
                      {ev.title}
                    </p>
                    {owner && (
                      <p
                        className="mt-0.5 text-[12.5px]"
                        style={{ fontFamily: 'Inter, system-ui, sans-serif', color: 'var(--ara-ink-muted)', fontWeight: 400 }}
                      >
                        {owner.name}
                      </p>
                    )}
                  </div>
                  <TextLink onClick={() => setEditingTask({ taskId: ev.id, mode: "edit" })}>
                    Edit
                  </TextLink>
                </li>
              );
            })}
          </ul>
        )}
      </EditorialSection>

      {/* ── How Ara helps ─────────────────────────────────────────── */}
      <EditorialSection
        testid="calendar-ara-helps"
        moduleMark={{ module: 'today', label: 'Ara' }}
        title="How Ara helps"
      >
        <p
          className="italic text-[16px] leading-[1.5]"
          style={{
            fontFamily: "var(--ara-font-display), 'Times New Roman', serif",
            color: 'var(--ara-ink)', fontWeight: 400, maxWidth: '48ch',
          }}
        >
          Ara reviews school notices, appointments and family events before they arrive on the diary — so the week ahead is never a surprise.
        </p>
        <div className="mt-6">
          <Link to="/home-os/command-centre" className="link-ara" data-testid="calendar-ask-ara">
            Ask ARA →
          </Link>
        </div>
      </EditorialSection>

      <Colophon>ARA · Planning</Colophon>

      <TaskEditModal
        open={!!editingTask}
        taskId={editingTask?.taskId}
        mode={editingTask?.mode || "edit"}
        presetDate={editingTask?.presetDate}
        onClose={() => { setEditingTask(null); if (editingTask?.mode === "add") toast.dismiss(); }}
      />
      <BetaFeedbackButton />
    </ResidenceLayout>
  );
}
